import type { ContactType } from '~/types/person/contacts';
import type { License } from '~/types/person/person';

export enum LicenseType {
    ESSENTIALS = 'essentials',
    PRO = 'pro',
    SIGNATURE = 'signature',
}

export type StorageAction = "set" | "get" | "remove";

export const LicenseTypeName: Record<LicenseType, string> = {
    [LicenseType.ESSENTIALS]: 'Essentials',
    [LicenseType.PRO]: 'Pro',
    [LicenseType.SIGNATURE]: 'Signature',
  };

// Tier ranking used to compare plans (essentials < pro < signature).
export const LicenseTypeRank: Record<LicenseType, number> = {
    [LicenseType.ESSENTIALS]: 1,
    [LicenseType.PRO]: 2,
    [LicenseType.SIGNATURE]: 3,
  };

// Returns the rank for a plan slug/name, or 0 when it can't be resolved.
const resolveLicenseRank = (value?: string | null): number => {
    if (!value) return 0;
    const key = value.toLowerCase() as LicenseType;
    return LicenseTypeRank[key] ?? 0;
};

// True when the target plan is a lower tier than the current plan (a downgrade).
// Returns false for upgrades, lateral/term-only changes, or unresolvable tiers.
export const isLicenseDowngrade = (current?: string | null, target?: string | null): boolean => {
    const currentRank = resolveLicenseRank(current);
    const targetRank = resolveLicenseRank(target);
    if (!currentRank || !targetRank) return false;
    return targetRank < currentRank;
};


export enum LicenseRenewalTerm {
    MONTHLY = 1,
    YEARLY = 12,
    TWO_YEARS = 24,
    THREE_YEARS = 36,
}

export const LicenseRenewalTermKey: Record<LicenseRenewalTerm, string> = {
    [LicenseRenewalTerm.MONTHLY]: 'monthly',
    [LicenseRenewalTerm.YEARLY]: 'yearly',
    [LicenseRenewalTerm.TWO_YEARS]: 'two_year',
    [LicenseRenewalTerm.THREE_YEARS]: 'three_year',
  };


// ✅ Instead of a second enum, use a Record typed by LicenseRenewalTerm
export const LicenseRenewalTermDisplay: Record<LicenseRenewalTerm, string> = {
    [LicenseRenewalTerm.MONTHLY]: 'Monthly',
    [LicenseRenewalTerm.YEARLY]: 'Yearly',
    [LicenseRenewalTerm.TWO_YEARS]: '2 Years',
    [LicenseRenewalTerm.THREE_YEARS]: '3 Years',
  };


export enum SubscriptionType {
    INDIVIDUAL = 1,
    COUPLES = 2,
    FAMILY = 4,
}

export interface PurchaseLicenseModalData {
    type: PurchaseType;
    license?: License;
    for?: number;
    single?: boolean;
    contact?: PurchaseLicenseFor; // Contact object for gift purchases
    promoCode?: string; // Promo code to apply automatically
}

export interface PurchaseLicenseFor {
    type: ContactType;
    id: number;
    name: string;
    is_self?: boolean;
}

export interface AssignLicenseModalData {
    license: License;
}

export type PricingData = {
    original: number;
    yearlyDiscount: number;
    unitPrice?: number;
};

export const PricingTable: Record<LicenseType, Record<LicenseRenewalTerm, PricingData>> = {
    [LicenseType.ESSENTIALS]: {
        [LicenseRenewalTerm.MONTHLY]: { original: 19.0, yearlyDiscount: 0.0, unitPrice: 19.0 },
        [LicenseRenewalTerm.YEARLY]: { original: 228.0, yearlyDiscount: 132.03, unitPrice: 95.97 },
        [LicenseRenewalTerm.TWO_YEARS]: { original: 456.0, yearlyDiscount: 287.05, unitPrice: 168.95 },
        [LicenseRenewalTerm.THREE_YEARS]: { original: 684.0, yearlyDiscount: 469.5, unitPrice: 214.5 },
    },
    [LicenseType.PRO]: {
        [LicenseRenewalTerm.MONTHLY]: { original: 39.0, yearlyDiscount: 0.0, unitPrice: 39.0 },
        [LicenseRenewalTerm.YEARLY]: { original: 468.0, yearlyDiscount: 271.0, unitPrice: 197.0 },
        [LicenseRenewalTerm.TWO_YEARS]: { original: 936.0, yearlyDiscount: 589.21, unitPrice: 346.79 },
        [LicenseRenewalTerm.THREE_YEARS]: { original: 1404.0, yearlyDiscount: 963.71, unitPrice: 440.29 },
    },
    [LicenseType.SIGNATURE]: {
        [LicenseRenewalTerm.MONTHLY]: { original: 0, yearlyDiscount: 0, unitPrice: 0 }, // No monthly plan for Signature
        [LicenseRenewalTerm.YEARLY]: { original: 1899.0, yearlyDiscount: 1099.63, unitPrice: 799.37 },
        [LicenseRenewalTerm.TWO_YEARS]: { original: 3798.0, yearlyDiscount: 2390.84, unitPrice: 1407.16 },
        [LicenseRenewalTerm.THREE_YEARS]: { original: 5697.0, yearlyDiscount: 3910.42, unitPrice: 1786.58 },
    },
};


// Multi-license discount structure matching API response
export const MULTI_LICENSE_DISCOUNTS: Record<number, number> = {
    1: 0, // No discount for 1st license
    2: 0.1, // 10% discount for 2nd license
    3: 0.12, // 12% discount for 3rd license
    4: 0.15, // 15% discount for 4th+ licenses
} as const;

// License benefits by type
export const LICENSE_BENEFITS: Record<LicenseType, string[]> = {
    [LicenseType.ESSENTIALS]: [
        '408 People Search Sites & People Search Affiliates',
        'Mostly Automated removals, with human support',
        'Unlimited emails, phones, addresses, and alt. names',
        'Standard priority: lower cost for non-urgent removals',
        'Continuous monitoring with full monthly re-scans',
        'Real-time dashboard to monitor removal efforts',
        'Monthly email report summarizing your protection',
    ],
    [LicenseType.PRO]: [
        'Everything in Essentials',
        '979 Data Brokers covered (+571 additional)',
        '178,274 Custom Sites covered for data removals',
        'Elevated removal priority for faster takedowns',
        'Digital footprint monitoring across major search engines',
        'Blur My House on Google Maps and Apple Maps',
        'Dark web monitoring and breach containment',
        'Paste site monitoring and removals',
        'Granular categorical controls for removals',
        'Mass-marketing opt-out & spam list suppression',
    ],
    [LicenseType.SIGNATURE]: [
        'Everything in Pro',
        'Expedited removal priority, usually same-day',
        'Exclusively US-based analysts handling removals',
        'Complex or multi-party exposure takedowns',
        'Social media PII exposure monitoring',
        'Removal from facial recognition databases',
        'Real estate listing photo removal',
        'Coordination with local or federal authorities',
        'PII threat hunting (human red team)',
        'Dark web / hacker forum monitoring',
        '24/7/365 critical exposure remediation',
        'Physical threat monitoring (travel safety)',
        'Escalation to our secure facility (SOC)',
    ],
} as const;

// Function to update the dynamic values
export const setLicenseStats = () => {
    const stats = useGlobalStore();
    const { statsData } = storeToRefs(stats);

    const formatNumber = (num: number) => num.toLocaleString('en-US');

    const essentialsCount = statsData.value?.data_broker_types.people_search! + statsData.value?.data_broker_types.people_search_affiliate!;
    const proDataBrokersCount = statsData.value?.data_brokers! + 3;
    const customSitesCount = statsData.value?.custom_sites!;
    const extra = proDataBrokersCount - essentialsCount;

    LICENSE_BENEFITS[LicenseType.ESSENTIALS][0] = `${formatNumber(essentialsCount)} People Search Sites & People Search Affiliates`;
    LICENSE_BENEFITS[LicenseType.PRO][1] = `${formatNumber(proDataBrokersCount)} Data Brokers covered (+${formatNumber(extra)} additional)`;
    LICENSE_BENEFITS[LicenseType.PRO][2] = `${formatNumber(customSitesCount)} Custom Sites covered for data removals`;
};

// Standard License Costs API Response Types
export interface MultiLicensePricing {
    count: number;
    price_per_license: number;
}

export interface TermPricing {
    term: 'monthly' | 'yearly' | 'two_year' | 'three_year';
    months: number;
    multi_licenses: MultiLicensePricing[];
}

export interface ExistingLicenses {
    monthly: number;
    yearly: number;
    two_year: number;
    three_year: number;
}

export interface LicenseStandardCosts {
    base_price_monthly: number;
    pricing: TermPricing[];
    existing_licenses: ExistingLicenses;
}

export interface StandardLicenseCostsResponse {
    [LicenseType.ESSENTIALS]: LicenseStandardCosts;
    [LicenseType.PRO]: LicenseStandardCosts;
    [LicenseType.SIGNATURE]: LicenseStandardCosts;
}

export enum PurchaseType {
    PURCHASE,
    UPGRADE,
    GIFT,
    REFUND,
}

export interface UpdateLicensePayload {
    renew: boolean;
}

export interface AssignLicensePayload {
    assigned_to_id: number;
    type: ContactType;
}

export interface RefundLicenseResponse {
    status: string;
    amount: number;
    amount_formatted: string;
    refunded_at: string;
    message: string;
}

export interface UpgradePricingResponse {
    license_id: number;
    current_license: {
        slug: string;
        name: string;
        price: number;
        discounted_price: number;
        term: number;
    };
    new_license: {
        slug: string;
        name: string;
        base_monthly_price: number;
    };
    calculation: {
        days_remaining: number;
        base_price: number;
        term_discount: number;
        term_discount_amount: number;
        discounted_price: number;
        existing_new_type_licenses: number;
        license_position: number;
        multi_license_discount: number;
        multi_license_discount_amount: number;
        full_discounted_price: number;
        prorated_new_license_charge: number;
        old_license_credit: number;
        final_amount_due: number;
        is_upgrade_to_longer_term?: boolean;
    };
    pricing: {
        subtotal: number;
        discounts: Array<{
            name: string;
            amount: number;
        }>;
        total: number;
    };
}
